export type NotificationCategory = string;
export type NotificationSource = null | string;

export interface NotificationApiItem {
  body_en: string;
  body_id: string;
  category: NotificationCategory;
  createdAt: string;
  employeeUserId?: number;
  id: string;
  isActionable: boolean;
  isRead: boolean;
  meta?: Record<string, unknown>;
  notificationType: string;
  notificanotificationTypeId: string;
  readAt: null | string;
  recipientEmployerId?: number;
  recipientHrId?: number;
  redirectParams?: null | Record<string, unknown>;
  redirectTarget?: null | string;
  source: NotificationSource;
  title_en: string;
  title_id: string;
}

export interface NotificationApiResponse {
  data: {
    notifications: NotificationApiItem[];
    unreadCount: number;
    pagination: {
      hasMore: boolean;
      limit: number;
      page: number;
    };
  };
  error: null;
  success: boolean;
}

export const normalizeNotificationCategory = (category?: string): string => {
  if (!category) return '';

  return category.toLowerCase();
};

export const normalizeNotificationSource = (source?: NotificationSource): string => {
  if (!source) return 'unknown';

  return source;
};

export const normalizeNotificationApiItem = (notification: NotificationApiItem): NotificationApiItem => ({
  ...notification,
  category: normalizeNotificationCategory(notification.category),
  source: normalizeNotificationSource(notification.source),
});

const buildDate = (baseDate: Date, daysAgo: number, hours: number, minutes: number): string => {
  const date = new Date(baseDate);
  date.setDate(baseDate.getDate() - daysAgo);
  date.setHours(hours, minutes, 0, 0);

  return date.toISOString();
};

export const getNotificationListResponse = (baseDate: Date = new Date()): NotificationApiResponse => ({
  success: true,
  data: {
    notifications: [
      {
        id: '1471ab1b-1b00-418a-9f26-7ea62046e321',
        notificationType: 'SD_DISBURSEMENT_REJECTED',
        notificanotificationTypeId: '1e369f88-3ca8-4f32-b721-f562f0ae25a7',
        recipientHrId: 324407,
        recipientEmployerId: 89959,
        createdAt: buildDate(baseDate, 0, 9, 0),
        title_en: 'Salary Disbursement payout rejected',
        body_en:
          'The Salary Disbursement payout was rejected for schedule number 6f0a2ff4-011b-4004-b157-6eb9c3238ee4. Click to open the Salary Disbursement page.',
        title_id: 'Pencairan Salary Disbursement ditolak',
        body_id:
          'Pencairan Salary Disbursement ditolak untuk nomor penjadwalan 6f0a2ff4-011b-4004-b157-6eb9c3238ee4. Klik untuk membuka halaman Salary Disbursement.',
        isActionable: false,
        isRead: false,
        readAt: null,
        category: 'message',
        source: 'push_notifications',
      },
      {
        id: '1471ab1b-1b00-418a-9f26-7ea62046e322',
        notificationType: 'TRANSACTION_STATUS_UPDATE',
        notificanotificationTypeId: '1e369f88-3ca8-4f32-b721-f562f0ae25a8',
        recipientHrId: 324407,
        recipientEmployerId: 89959,
        createdAt: buildDate(baseDate, 0, 8, 15),
        title_en: 'Transaction update available',
        body_en: 'Your latest transaction status has been updated. Open the transaction page for more details.',
        title_id: 'Pembaruan transaksi tersedia',
        body_id: 'Status transaksi terbarumu sudah diperbarui. Buka halaman transaksi untuk melihat detailnya.',
        isActionable: false,
        isRead: true,
        readAt: buildDate(baseDate, 0, 8, 45),
        category: 'informasi',
        source: 'transaction',
      },
      {
        id: '1471ab1b-1b00-418a-9f26-7ea62046e323',
        notificationType: 'EWA_STATUS_UPDATE',
        notificanotificationTypeId: '1e369f88-3ca8-4f32-b721-f562f0ae25a9',
        recipientHrId: 324407,
        recipientEmployerId: 89959,
        createdAt: buildDate(baseDate, 1, 9, 0),
        title_en: 'EWA status has changed',
        body_en: 'There is a new update to your EWA status. Open the app to review the latest status.',
        title_id: 'Status EWA berubah',
        body_id: 'Ada pembaruan pada status EWA kamu. Buka aplikasi untuk melihat status terbarunya.',
        isActionable: false,
        isRead: false,
        readAt: null,
        category: 'informasi',
        source: 'status_ewa',
      },
      {
        id: '1471ab1b-1b00-418a-9f26-7ea62046e324',
        notificationType: 'MESSAGE_CENTER_UPDATE',
        notificanotificationTypeId: '1e369f88-3ca8-4f32-b721-f562f0ae25b0',
        recipientHrId: 324407,
        recipientEmployerId: 89959,
        createdAt: buildDate(baseDate, 1, 7, 30),
        title_en: 'A new message has arrived',
        body_en: 'You have received a new message in your inbox. Open the message center to read it.',
        title_id: 'Ada pesan baru masuk',
        body_id: 'Kamu menerima pesan baru di kotak masuk. Buka pusat pesan untuk membacanya.',
        isActionable: false,
        isRead: true,
        readAt: buildDate(baseDate, 1, 8, 5),
        category: 'message',
        source: 'push_notifications',
      },
      {
        id: '1471ab1b-1b00-418a-9f26-7ea62046e325',
        notificationType: 'INTERNAL_SETTINGS_UPDATE',
        notificanotificationTypeId: '1e369f88-3ca8-4f32-b721-f562f0ae25b1',
        recipientHrId: 324407,
        recipientEmployerId: 89959,
        createdAt: buildDate(baseDate, 4, 9, 0),
        title_en: 'Notification settings were updated',
        body_en: 'Review the latest notification preferences and keep the channels that matter to you turned on.',
        title_id: 'Pengaturan notifikasi diperbarui',
        body_id: 'Tinjau preferensi notifikasi terbaru dan tetap aktifkan kanal yang penting untukmu.',
        isActionable: false,
        isRead: true,
        readAt: buildDate(baseDate, 4, 9, 20),
        category: 'informasi',
        source: 'internal_settings',
      },
    ],
    unreadCount: 2,
    pagination: {
      page: 1,
      limit: 10,
      hasMore: true,
    },
  },
  error: null,
});
